import { toSafeString } from '../utils/toSafeString.js';
import { PIEVE_MCP_TOOL_PREFIX } from './claudeCodeTools.js';
import { PIEVE_HARNESS_CLAUDE_CODE } from './planRuns.js';

const labels = (running, completed) => ({ labels: { running, completed } });
const HIDDEN = Object.freeze({ hidden: true });

/* How each Claude Code tool shows in a Pieve thread, one entry per tool. `pieveTool` shows it as the Pieve tool that
   does the same work, `labels` names a step Pieve has no tool for, and `hidden` keeps it out of the feed. The feed,
   the share page, the tool inspector and the thread review all read this table, through toPieveEquivalentToolCall
   for names and through getPieveToolCallDisplayText for labels.

   Hidden: Claude Code's to-do list (TodoWrite, and the Task* tools that replace it), as Pieve keeps its own checklist
   updates out of the feed; its question, which the question card shows; and the plan run's wait for answers. */
export const CLAUDE_CODE_TOOL_DISPLAY = Object.freeze({
  Bash: { pieveTool: 'shell_run' },
  BashOutput: labels('Checking a running command', 'Checked a running command'),
  KillShell: labels('Stopping a running command', 'Stopped a running command'),
  KillBash: labels('Stopping a running command', 'Stopped a running command'),
  Read: { pieveTool: 'files_read' },
  NotebookRead: { pieveTool: 'files_read' },
  Edit: { pieveTool: 'files_write' },
  MultiEdit: { pieveTool: 'files_write' },
  Write: { pieveTool: 'files_write' },
  NotebookEdit: { pieveTool: 'files_write' },
  Glob: { pieveTool: 'search_ripgrep' },
  Grep: { pieveTool: 'search_ripgrep' },
  LS: { pieveTool: 'files_list' },
  WebFetch: { pieveTool: 'read_web_page' },
  WebSearch: { pieveTool: 'web_search' },
  Agent: { pieveTool: 'create_subagent' },
  Task: { pieveTool: 'create_subagent' },
  ToolSearch: labels('Searching for available tools', 'Searched for available tools'),
  RefreshMcpTools: labels('Refreshing available tools', 'Refreshed available tools'),
  EnterPlanMode: labels('Starting a plan', 'Started a plan'),
  ExitPlanMode: labels('Sharing the plan', 'Shared the plan'),
  Skill: labels('Using a skill', 'Used a skill'),
  SlashCommand: labels('Running a saved command', 'Ran a saved command'),
  ListMcpResources: labels('Checking available resources', 'Checked available resources'),
  ListMcpResourcesTool: labels('Checking available resources', 'Checked available resources'),
  ReadMcpResource: labels('Reading a resource', 'Read a resource'),
  ReadMcpResourceTool: labels('Reading a resource', 'Read a resource'),
  ReadMcpResourceDir: labels('Reading a resource folder', 'Read a resource folder'),
  ReadMcpResourceDirTool: labels('Reading a resource folder', 'Read a resource folder'),
  TodoWrite: HIDDEN,
  TaskCreate: HIDDEN,
  TaskGet: HIDDEN,
  TaskUpdate: HIDDEN,
  TaskList: HIDDEN,
  AskUserQuestion: HIDDEN,
  [`${PIEVE_MCP_TOOL_PREFIX}pieve_interactions_wait`]: HIDDEN
});

/* Plan runs store Claude Code's own tool names (passthrough phase 2); `harness` on the stored event says so. Every
   surface turns a plan run's tool name through here: Pieve's own tools reach Claude Code through its MCP client as
   `mcp__pieve__<Pieve tool>`, so they keep their Pieve name and their Pieve rendering, and Claude Code's built-in
   tools read as CLAUDE_CODE_TOOL_DISPLAY says. A tool from any other run passes through unchanged. */
const toPieveEquivalentToolCall = ({ toolName, args, harness } = {}) => {
  const safeToolName = toSafeString(toolName);
  const safeArgs = args && typeof args === 'object' && !Array.isArray(args) ? args : {};

  if (harness !== PIEVE_HARNESS_CLAUDE_CODE) {
    return { toolName: safeToolName, args: safeArgs, hidden: false };
  }

  const display = Object.hasOwn(CLAUDE_CODE_TOOL_DISPLAY, safeToolName) ? CLAUDE_CODE_TOOL_DISPLAY[safeToolName] : null;

  if (display?.hidden) {
    return { toolName: safeToolName, args: safeArgs, hidden: true };
  }

  if (safeToolName.startsWith(PIEVE_MCP_TOOL_PREFIX)) {
    return { toolName: safeToolName.slice(PIEVE_MCP_TOOL_PREFIX.length), args: safeArgs, hidden: false };
  }

  const pieveToolName = display?.pieveTool;

  if (!pieveToolName) {
    return { toolName: safeToolName, args: safeArgs, hidden: false };
  }

  // Pieve's file search reads its pattern as `query`.
  if (pieveToolName === 'search_ripgrep') {
    return { toolName: pieveToolName, args: { ...safeArgs, query: toSafeString(safeArgs.pattern) || undefined }, hidden: false };
  }

  // A sub-agent line names its task.
  if (pieveToolName === 'create_subagent' && toSafeString(safeArgs.description)) {
    return { toolName: pieveToolName, args: { ...safeArgs, displayIntent: { action: 'creating', subject: `a helper (${toSafeString(safeArgs.description)})` } }, hidden: false };
  }

  return { toolName: pieveToolName, args: safeArgs, hidden: false };
};

export default toPieveEquivalentToolCall;
